import { type CatalogSet, type CatalogSetItem } from '@kalos/kalos-rpc';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { InventoryClientService } from '../../tools/helpers';
import { queryKeys } from './constants';

// Use the actual protobuf types
export type ToolSet = CatalogSet;
export type ToolSetItem = CatalogSetItem;

// Extended interface for display purposes (includes department name)
export interface ToolSetWithDepartmentName extends ToolSet {
  departmentName: string;
}

// Utility functions to work with the new protobuf structure
export const getCatalogItemIds = (toolSet: ToolSet): number[] => {
  return (
    toolSet.items
      ?.filter((item: CatalogSetItem) => item.isActive)
      .map((item: CatalogSetItem) => item.catalogItemId) || []
  );
};

export const getCatalogItemQuantities = (toolSet: ToolSet): Record<number, number> => {
  const quantities: Record<number, number> = {};
  toolSet.items
    ?.filter((item: CatalogSetItem) => item.isActive)
    .forEach((item: CatalogSetItem) => {
      quantities[item.catalogItemId] = item.quantity || 1; // Default to 1 if not set
    });
  return quantities;
};

// Helper function to get catalog item IDs from quantities object (only items with quantity > 0)
export const getCatalogItemIdsFromQuantities = (quantities: Record<number, number>): number[] => {
  return Object.keys(quantities)
    .map(Number)
    .filter((id) => quantities[id] > 0);
};

// Helper function to create quantities object from catalog item IDs (with default quantity 1)
export const createQuantitiesFromItemIds = (itemIds: number[]): Record<number, number> => {
  const quantities: Record<number, number> = {};
  itemIds.forEach((id) => {
    quantities[id] = 1; // Default quantity is 1 when selected
  });
  return quantities;
};

export const createToolSetWithCatalogItemIds = (
  toolSet: ToolSet,
  quantities: Record<number, number>,
): ToolSet => {
  const catalogItemIds = Object.keys(quantities).map(Number);
  return {
    ...toolSet,
    items: catalogItemIds.map((catalogItemId) => ({
      id: 0, // Will be set by the server
      catalogSetId: toolSet.id || 0,
      catalogItemId,
      createdAt: '',
      updatedAt: '',
      isActive: true,
      quantity: quantities[catalogItemId] || 1,
      fieldMask: [],
      pageNumber: 0,
      orderBy: '',
      orderDir: '',
      includeCatalogItem: false,
    })),
    includeItems: true,
  };
};

export interface ToolSetFilter {
  name?: string;
  departmentId?: number;
  pageNumber?: number;
  orderBy?: string;
  orderDir?: string;
}

export interface CreateToolSetRequest {
  name: string;
  departmentId: number;
  catalogItemQuantities: Record<number, number>; // Changed from catalogItemIds + catalogItemQuantities
  createdById?: number;
}

export interface UpdateToolSetRequest {
  id: number;
  name: string;
  catalogItemQuantities: Record<number, number>; // Changed from catalogItemIds + catalogItemQuantities
}

// API filter interface for tool sets
export interface ToolSetApiFilter {
  name?: string;
  departmentId?: number;
  pageNumber?: number;
  pageSize?: number;
  orderBy?: string;
  orderDir?: string;
}

// Hook to get tool sets with filtering
export const useToolSetsQuery = (filter: ToolSetFilter = {}) => {
  return useQuery({
    queryKey: [queryKeys.toolSet.root, 'list', filter],
    queryFn: async () => {
      const client = InventoryClientService;

      // Create the filter object for the API
      const apiFilter: ToolSetApiFilter = {
        pageNumber: filter.pageNumber || 0,
        pageSize: 50, // Default page size
      };

      if (filter.name) {
        apiFilter.name = `%${filter.name}%`;
      }

      if (filter.departmentId) {
        apiFilter.departmentId = filter.departmentId;
      }

      if (filter.orderBy) {
        apiFilter.orderBy = filter.orderBy;
      }

      if (filter.orderDir) {
        apiFilter.orderDir = filter.orderDir;
      }

      // Call the actual API
      const response = await client.BatchGetCatalogSet({
        id: 0,
        departmentId: apiFilter.departmentId || 0,
        createdById: 0,
        name: apiFilter.name || '',
        description: '',
        createdAt: '',
        updatedAt: '',
        isActive: true, // Only fetch active tool sets
        fieldMask: [],
        pageNumber: apiFilter.pageNumber || 0,
        orderBy: apiFilter.orderBy || '',
        orderDir: apiFilter.orderDir || '',
        items: [],
        includeItems: true,
        departmentName: '',
      });

      return {
        results: response.results || [],
        totalCount: response.totalCount || 0,
      };
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Hook to get a single tool set by ID
export const useToolSetQuery = (id: number) => {
  return useQuery({
    queryKey: [queryKeys.toolSet.root, 'details', id],
    queryFn: async () => {
      const client = InventoryClientService;

      const response = await client.GetCatalogSet({
        id,
        departmentId: 0,
        createdById: 0,
        name: '',
        description: '',
        createdAt: '',
        updatedAt: '',
        isActive: true, // Only fetch active tool sets
        fieldMask: [],
        pageNumber: 0,
        orderBy: '',
        orderDir: '',
        items: [],
        includeItems: true,
        departmentName: '',
      });

      return response;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Hook to create a new tool set
export const useCreateToolSetMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateToolSetRequest) => {
      const client = InventoryClientService;
      console.log('Create Data: ', data);
      console.log('Catalog Item Quantities: ', data.catalogItemQuantities);

      // Step 1: Create the CatalogSet (without items)
      const newToolSet: ToolSet = {
        id: 0, // Will be set by the server
        name: data.name,
        departmentId: data.departmentId,
        createdById: 0, // Backend will set from authenticated user
        description: '',
        createdAt: '',
        updatedAt: '',
        isActive: true,
        fieldMask: [],
        pageNumber: 0,
        orderBy: '',
        orderDir: '',
        items: [], // Empty - items will be created separately
        includeItems: false,
        departmentName: '',
      };

      const createdSet = await client.CreateCatalogSet(newToolSet);

      // Step 2: Create CatalogSetItem records for each catalog item ID
      const catalogItemIds = Object.keys(data.catalogItemQuantities)
        .map(Number)
        .filter((id) => data.catalogItemQuantities[id] > 0); // Only include items with quantity > 0
      console.log('Catalog Item IDs: ', catalogItemIds);
      if (catalogItemIds.length > 0) {
        const itemPromises = catalogItemIds.map((catalogItemId) => {
          const quantity = data.catalogItemQuantities[catalogItemId] || 1;
          console.log(`Creating item ${catalogItemId} with quantity ${quantity}`);
          return client.CreateCatalogSetItem({
            id: 0, // Will be set by the server
            catalogSetId: createdSet.id,
            catalogItemId,
            createdAt: '',
            updatedAt: '',
            isActive: true,
            quantity,
            fieldMask: [],
            pageNumber: 0,
            orderBy: '',
            orderDir: '',
            includeCatalogItem: false,
          });
        });

        await Promise.all(itemPromises);
      }

      return createdSet;
    },
    onMutate: async (data: CreateToolSetRequest) => {
      // Cancel any outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({
        queryKey: [queryKeys.toolSet.root, 'list'],
      });

      // Snapshot the previous value
      const previousQueries = queryClient.getQueriesData({
        queryKey: [queryKeys.toolSet.root, 'list'],
      });

      // Create a temporary ID for the optimistic update
      const tempId = Date.now(); // Use timestamp as temporary ID

      // Create optimistic tool set
      const optimisticToolSet: ToolSet = {
        id: tempId,
        departmentId: data.departmentId,
        createdById: 0, // Backend will set from authenticated user
        name: data.name,
        description: '',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        isActive: true,
        fieldMask: [],
        pageNumber: 0,
        orderBy: '',
        orderDir: '',
        items: [],
        includeItems: false,
        departmentName: '',
      };

      // Optimistically add the new tool set to all cached queries
      queryClient.setQueriesData({ queryKey: [queryKeys.toolSet.root, 'list'] }, (old: any) => {
        if (!old) return old;

        return {
          ...old,
          results: [optimisticToolSet, ...(old.results || [])],
          totalCount: (old.totalCount || 0) + 1,
        };
      });

      // Return context for rollback and ID mapping
      return { previousQueries, tempId, optimisticToolSet };
    },
    onSuccess: (data, variables, context) => {
      // Update the optimistic entry with the real server data
      queryClient.setQueriesData({ queryKey: [queryKeys.toolSet.root, 'list'] }, (old: any) => {
        if (!old) return old;

        return {
          ...old,
          results:
            old.results?.map((toolSet: ToolSet) =>
              toolSet.id === context?.tempId ? data : toolSet,
            ) || [],
        };
      });
    },
    onError: (err, variables, context) => {
      // If the mutation fails, roll back to previous state
      if (context?.previousQueries) {
        context.previousQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    onSettled: () => {
      // Always refetch after error or success to ensure we have the latest data
      queryClient.invalidateQueries({
        queryKey: [queryKeys.toolSet.root, 'list'],
        exact: false,
      });
    },
  });
};

// Hook to update an existing tool set
export const useUpdateToolSetMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateToolSetRequest) => {
      const client = InventoryClientService;
      console.log('Update Data: ', data);
      console.log('Update Catalog Item Quantities: ', data.catalogItemQuantities);

      // Step 1: Update the CatalogSet (name only)
      const existingSet = await client.GetCatalogSet({
        id: data.id,
        departmentId: 0,
        createdById: 0,
        name: '',
        description: '',
        createdAt: '',
        updatedAt: '',
        isActive: true,
        fieldMask: [],
        pageNumber: 0,
        orderBy: '',
        orderDir: '',
        items: [],
        includeItems: true,
        departmentName: '',
      });

      const updatedSet = await client.UpdateCatalogSet({
        ...existingSet,
        name: data.name,
        fieldMask: ['name'],
        items: [], // Don't include items in update
        includeItems: false,
      });

      // Step 2: Handle CatalogSetItem updates (soft delete/activate pattern)
      const existingItemIds = existingSet.items?.map((item) => item.catalogItemId) || [];
      const newItemIds = Object.keys(data.catalogItemQuantities)
        .map(Number)
        .filter((id) => data.catalogItemQuantities[id] > 0); // Only include items with quantity > 0

      // Items to deactivate (exist but not in new list)
      const itemsToDeactivate =
        existingSet.items?.filter((item) => !newItemIds.includes(item.catalogItemId)) || [];

      // Items to activate (exist but were inactive)
      const itemsToActivate =
        existingSet.items?.filter(
          (item) => newItemIds.includes(item.catalogItemId) && !item.isActive,
        ) || [];

      // Items to create (completely new)
      const itemsToCreate = newItemIds.filter((id) => !existingItemIds.includes(id));

      // Items to update quantities (exist and are active)
      const itemsToUpdateQuantities =
        existingSet.items?.filter(
          (item) =>
            newItemIds.includes(item.catalogItemId) &&
            item.isActive &&
            item.quantity !== data.catalogItemQuantities[item.catalogItemId],
        ) || [];

      // Deactivate items that are no longer needed
      if (itemsToDeactivate.length > 0) {
        const deactivatePromises = itemsToDeactivate.map((item) =>
          client.UpdateCatalogSetItem({
            ...item,
            isActive: false,
            fieldMask: ['isActive'],
          }),
        );
        await Promise.all(deactivatePromises);
      }

      // Activate items that were previously inactive
      if (itemsToActivate.length > 0) {
        const activatePromises = itemsToActivate.map((item) =>
          client.UpdateCatalogSetItem({
            ...item,
            isActive: true,
            fieldMask: ['isActive'],
          }),
        );
        await Promise.all(activatePromises);
      }

      // Update quantities of existing items
      if (itemsToUpdateQuantities.length > 0) {
        console.log('Items to update quantities:', itemsToUpdateQuantities);
        const updateQuantityPromises = itemsToUpdateQuantities.map((item) => {
          const newQuantity = data.catalogItemQuantities[item.catalogItemId] || 1;
          console.log(
            `Updating quantity for item ${item.catalogItemId} from ${item.quantity} to ${newQuantity}`,
          );
          return client.UpdateCatalogSetItem({
            ...item,
            quantity: newQuantity,
            fieldMask: ['quantity'],
          });
        });
        await Promise.all(updateQuantityPromises);
      }

      // Create new items
      if (itemsToCreate.length > 0) {
        console.log('Items to create:', itemsToCreate);
        const createPromises = itemsToCreate.map((catalogItemId) => {
          const quantity = data.catalogItemQuantities[catalogItemId] || 1;
          console.log(`Creating new item ${catalogItemId} with quantity ${quantity}`);
          return client.CreateCatalogSetItem({
            id: 0, // Will be set by the server
            catalogSetId: data.id,
            catalogItemId,
            createdAt: '',
            updatedAt: '',
            isActive: true,
            quantity,
            fieldMask: [],
            pageNumber: 0,
            orderBy: '',
            orderDir: '',
            includeCatalogItem: false,
          });
        });
        await Promise.all(createPromises);
      }

      return updatedSet;
    },
    onSuccess: (data) => {
      // Invalidate and refetch tool sets
      queryClient.invalidateQueries({
        queryKey: [queryKeys.toolSet.root, 'list'],
        exact: false, // This will invalidate all queries that start with this key
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.toolSet.root, 'details', data.id],
      });
    },
  });
};

// Hook to delete a tool set
export const useDeleteToolSetMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const client = InventoryClientService;

      // Step 1: Get the tool set to find associated items
      const existingSet = await client.GetCatalogSet({
        id,
        departmentId: 0,
        createdById: 0,
        name: '',
        description: '',
        createdAt: '',
        updatedAt: '',
        isActive: true,
        fieldMask: [],
        pageNumber: 0,
        orderBy: '',
        orderDir: '',
        items: [],
        includeItems: true,
        departmentName: '',
      });

      // Step 2: Soft delete all associated CatalogSetItem records
      if (existingSet.items && existingSet.items.length > 0) {
        const deactivatePromises = existingSet.items.map((item) =>
          client.UpdateCatalogSetItem({
            ...item,
            isActive: false,
            fieldMask: ['isActive'],
          }),
        );
        await Promise.all(deactivatePromises);
      }

      // Step 3: Soft delete the CatalogSet itself
      await client.UpdateCatalogSet({
        ...existingSet,
        isActive: false,
        fieldMask: ['isActive'],
        items: [],
        includeItems: false,
      });

      return { id, deletedSet: existingSet };
    },
    onMutate: async (id: number) => {
      // Cancel any outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({
        queryKey: [queryKeys.toolSet.root, 'list'],
      });

      // Snapshot the previous value
      const previousQueries = queryClient.getQueriesData({
        queryKey: [queryKeys.toolSet.root, 'list'],
      });

      // Optimistically update all tool set list queries
      queryClient.setQueriesData({ queryKey: [queryKeys.toolSet.root, 'list'] }, (old: any) => {
        if (!old) return old;

        return {
          ...old,
          results: old.results?.filter((toolSet: ToolSet) => toolSet.id !== id) || [],
          totalCount: Math.max(0, (old.totalCount || 0) - 1),
        };
      });

      // Return a context object with the snapshotted value
      return { previousQueries };
    },
    onError: (err, id, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousQueries) {
        context.previousQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    onSettled: () => {
      // Always refetch after error or success to ensure we have the latest data
      queryClient.invalidateQueries({
        queryKey: [queryKeys.toolSet.root, 'list'],
        exact: false,
      });
    },
  });
};
